
import { useState, useMemo, useCallback } from 'react'
import { useLocation } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { Icon } from '@/components/ui/icon'
import { FolderGroup } from './folder-group'
import { Folder } from './folder-group/types'
import { FOLDER_PROJECTS } from './folder-data'
import { useSharedProjects } from '@/providers/ProjectsProvider'
import { CreateFolderModal, DeleteFolderModal } from './modals'

export const SidebarNavigation: React.FC = () => {
  const { pathname } = useLocation()

  // Use real Projects API
  const {
    projects: apiProjects,
    isLoading,
    error,
    createProject,
    updateProject: updateProjectAPI,
    deleteProject,
    reorderProjects,
  } = useSharedProjects()

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [folderToDelete, setFolderToDelete] = useState<{
    id: string
    name: string
    categoryId: string
  } | null>(null)
  const [activeCategoryId, setActiveCategoryId] = useState<string>('')

  // Convert API categories to Folder format and put them in Project category
  const folderProjects = useMemo(() => {
    const projectFolders =
      apiProjects?.map((project) => ({
        id: project.uuid,
        name: project.name,
        href: `/workspace/project/${project.uuid}`,
        canDelete: apiProjects.length > 1, // Only allow delete if more than 1 category
        canRename: true,
        isDraggable: true,
      })) || []

    // Update Project category to include API categories as items
    return FOLDER_PROJECTS.map((project) => {
      if (project.id === 'projects') {
        return {
          ...project,
          items: projectFolders, // Replace mock data with API categories
        }
      }
      return project
    })
  }, [apiProjects])

  // Mark active folder based on current pathname
  const markActiveFolder = useCallback((folders: Folder[]) =>
    folders.map((folder) => ({
      ...folder,
      isActive: pathname === folder.href,
    })), [pathname])

  const handleFolderRename = useCallback(async (folderId: string, newName: string, categoryId: string) => {
    // Only handle rename for projects category items (which are API categories)
    if (categoryId === 'projects') {
      await updateProjectAPI(folderId, { name: newName })
      // Error handling is done in the hook with toast notifications
    }
  }, [updateProjectAPI])

  const handleFolderDelete = useCallback((folderId: string, categoryId: string) => {
    // Only handle delete for projects category items (which are API categories)
    if (categoryId === 'projects') {
      const categoryToDelete = apiProjects?.find((c) => c.uuid === folderId)
      if (categoryToDelete) {
        setFolderToDelete({ id: folderId, name: categoryToDelete.name, categoryId })
        setIsDeleteModalOpen(true)
      }
    }
  }, [apiProjects])

  const handleDeleteConfirm = useCallback(async () => {
    if (folderToDelete) {
      await deleteProject(folderToDelete.id) // Use folderId which is the category UUID
      // Error handling is done in the hook with toast notifications
      setFolderToDelete(null)
    }
  }, [folderToDelete, deleteProject])

  const handleFolderMove = useCallback(async (fromIndex: number, toIndex: number, categoryId: string) => {
    // Only handle move for projects project items (which are API projects)
    if (categoryId === 'projects' && apiProjects) {
      const currentItems = [...apiProjects]
      const [moved] = currentItems.splice(fromIndex, 1)

      if (moved) {
        currentItems.splice(toIndex, 0, moved)

        // Create the new order array with UUIDs and order numbers
        const projectOrders = currentItems.map((project, index) => ({
          uuid: project.uuid,
          order: index
        }))

        // Optimistic update: Update local state immediately
        // This will update the UI instantly without waiting for API
        // The useProjects hook should handle optimistic updates internally

        try {
          await reorderProjects(projectOrders)
          // Success - optimistic update is now confirmed
        } catch (error) {
          // Error - the hook should revert the optimistic update
          console.error('Failed to reorder projects:', error)
        }
      }
    }
  }, [apiProjects, reorderProjects])

  const handleAddFolder = useCallback((categoryId: string) => {
    // Only handle add for projects category
    if (categoryId === 'projects') {
      setActiveCategoryId(categoryId)
      setIsCreateModalOpen(true)
    }
  }, [])

  const handleCreateFolder = useCallback(async (folderName: string) => {
    await createProject(folderName)
    // Error handling is done in the hook with toast notifications
  }, [createProject])

  return (
    <div className="flex h-full flex-col gap-y-4">
      {/* Static Navigation Links */}
      <ul className="mb-2 flex flex-col gap-0 text-xs">
        <li>
          <Link
            to="/workspace/recents"
            className={`flex items-center gap-2 rounded-md p-2 ${
              pathname === '/workspace' || pathname === '/workspace/recents'
                ? 'bg-muted font-semibold text-foreground'
                : 'text-foreground hover:bg-accent hover:text-foreground'
            }`}
          >
            <Icon name="refresh-cw" />
            <span>Recents</span>
          </Link>
        </li>
        <li>
          <Link
            to="/workspace/drafts"
            className={`flex items-center gap-2 rounded-md p-2 ${
              pathname === '/workspace/drafts'
                ? 'bg-muted font-semibold text-foreground'
                : 'text-foreground hover:bg-accent hover:text-foreground'
            }`}
          >
            <Icon name="pencil" />
            <span>Drafts</span>
          </Link>
        </li>
        <li>
          <Link
            to="/workspace/trash"
            className={`flex items-center gap-2 rounded-md p-2 ${
              pathname === '/workspace/trash'
                ? 'bg-muted font-semibold text-foreground'
                : 'text-foreground hover:bg-accent hover:text-foreground'
            }`}
          >
            <Icon name="trash" />
            <span>Trash</span>
          </Link>
        </li>
      </ul>

      {/* Dynamic Folder Projects */}
      {isLoading ? (
        <div className="px-2 py-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-b border-current"></div>
            Loading projects...
          </div>
        </div>
      ) : error ? (
        <div className="px-2 py-4">
          <div className="text-xs text-red-500">Error loading projects: {error}</div>
        </div>
      ) : (
        folderProjects.map((project) => (
          <FolderGroup
            key={project.id}
            title={project.title}
            categoryId={project.id}
            folders={markActiveFolder(project.items)}
            isUpdating={isLoading}
            showAddButton={project.showAddButton}
            onFolderRename={handleFolderRename}
            onFolderDelete={handleFolderDelete}
            onFolderMove={handleFolderMove}
            onAddFolder={handleAddFolder}
          />
        ))
      )}

      <CreateFolderModal
        isOpen={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onCreate={handleCreateFolder}
        isCreating={isLoading}
      />

      <DeleteFolderModal
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onDeleteConfirm={handleDeleteConfirm}
        folderName={folderToDelete?.name || ''}
      />
    </div>
  )
}
